import React, {forwardRef, useContext} from 'react';
import AppContext from "../context/AppContext";
import SecureFetchContext from "../context/SecureFetchContext";
import getApiRoute from "../routes/apiRoutes";
import Menu from "@material-ui/core/Menu";
import Fade from "@material-ui/core/Fade";
import MenuItem from "@material-ui/core/MenuItem";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Box from "@material-ui/core/Box";
import StarIcon from "@material-ui/icons/Star";
import StarBorderIcon from "@material-ui/icons/StarBorder";
import PropTypes from "prop-types";
import {useTranslation} from "react-i18next";
import {getAllPickups} from "../helpers";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";

const PickupAddressSelector = forwardRef(({children, asSelect, onSelect, pickup, ...rest}, ref) => {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const { appValues, setAppValues } = useContext(AppContext);
    const secureFetch = useContext(SecureFetchContext);
    const { t } = useTranslation();

    const handleClose = () => {
        setAnchorEl(null);
    };

    const isDefault = (addr) => appValues.user.address && addr.address.id === appValues.user.address.id;

    const handleSetDefault = (event, addr) => {
        event.stopPropagation();
        if (isDefault(addr)) {
            return;
        }
        secureFetch(getApiRoute('user_pickup_address_id', [addr.id, 'default']), null, 'PUT').then(() => {
            secureFetch(getApiRoute('user')).then(json => {
                setAppValues({...appValues, user: json});
            });
        });
    };

    const renderStar = (addr) => {
        const current = isDefault(addr);
        return (
            <Tooltip title={current ? t('messages.default_pickup_address') : t('messages.set_as_default_pickup_address')}>
                <span onClick={(e) => e.stopPropagation()}>
                    <IconButton
                        size="small"
                        onClick={(e) => handleSetDefault(e, addr)}
                        disabled={current}
                        aria-label="set-as-default"
                    >
                        {current ? <StarIcon fontSize="small" color="primary"/> : <StarBorderIcon fontSize="small"/>}
                    </IconButton>
                </span>
            </Tooltip>
        );
    };

    const getProps = (child) => {
        let pProps = { ref: ref, ...rest};
        pProps.onClick = (event) => setAnchorEl(event.currentTarget) ;
        return pProps;
    }

    const childrenWithProps = React.Children.map(children, child => {
        // Checking isValidElement is the safe way and avoids a typescript error too.
        if (React.isValidElement(child)) {
            return React.cloneElement(child, getProps(child));
        }
        return child;
    });

    return (
        <React.Fragment>
            {asSelect ?
                <FormControl fullWidth>
                    <Select
                        value={pickup.id}
                        name="pickup"
                        onChange={(event) => {
                            const pickup = getAllPickups(appValues.user).find(addr => addr.id === event.target.value);
                            onSelect(pickup);
                        }}
                    >
                        {getAllPickups(appValues.user).map((option) => (
                            <MenuItem
                                key={option.id}
                                value={option.id}
                            >
                                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                    <span>{option.address.city}</span>
                                    {renderStar(option)}
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                : <div>
                    {[childrenWithProps].map(el => el)}
                    <Menu
                        id="fade-menu"
                        anchorEl={anchorEl}
                        keepMounted
                        open={open}
                        onClose={handleClose}
                        TransitionComponent={Fade}
                        transformOrigin={{
                            horizontal: 'center',
                            vertical: 'top',
                        }}
                    >
                        {
                            getAllPickups(appValues.user).map((addr, idx) => (
                                <MenuItem onClick={() => {
                                    onSelect(addr);
                                    handleClose();
                                }} key={idx}>
                                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                        <span>{addr.address.city}</span>
                                        {renderStar(addr)}
                                    </Box>
                                </MenuItem>
                            ))
                        }
                    </Menu>
                </div>
            }
        </React.Fragment>
    );
});

PickupAddressSelector.propTypes = {
    asSelect: PropTypes.bool
};

export default PickupAddressSelector;
